import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import { CopyButton } from "@/components/CopyButton";
import { KeyOnceModal } from "@/components/KeyOnceModal";
import { useAuth } from "@/contexts/AuthContext";
import { awaitingFirstRequest, useAcceptedModels, useOnboarding } from "@/api/hooks/use-onboarding";
import { useRotateGatewayKey, useSaveProviderKey } from "@/api/hooks/use-settings-keys";
import { toUserMessage } from "@/lib/errors";
import { buildAgentPrompt, buildSnippets, maskKey, snippetModels, type Snippet } from "@/onboarding/snippets";
import { PROVIDERS } from "@/lib/providers";

const PROVIDER_LABELS = new Map<string, string>(PROVIDERS.map((p) => [p.id, p.label]));

type StepState = "done" | "current" | "pending";

/** 10080 -> "7 days", 90 -> "90 minutes": the configured length, exactly. */
function lengthLabel(minutes: number): string {
  const [count, unit] =
    minutes % 1440 === 0 ? [minutes / 1440, "day"] : minutes % 60 === 0 ? [minutes / 60, "hour"] : [minutes, "minute"];
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

const inputCls =
  "rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40";
const primaryCls =
  "rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:cursor-not-allowed disabled:opacity-40";

/**
 * A new Self-serve Customer's admin, from an empty account to the first
 * request, in one place (self-serve ticket 17):
 *
 *   1. a provider key, checked live and stored encrypted;
 *   2. the gateway key (shown once), a prompt that has the Customer's coding
 *      agent make the switch, and copy-paste setup for the OpenAI and
 *      Anthropic SDKs and Claude Code (each folded until clicked), with model
 *      names the account accepts;
 *   3. a live wait for the first request, which starts the Free week, or a
 *      Priority Customer's Benchmark (ADR-0004).
 *
 * Shown until the first request lands; Customers set up by Phantm's team keep
 * the OnboardingGuide.
 */
export function OnboardingWizard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: status } = useOnboarding(isAdmin);
  const selfSignup = Boolean(status?.self_signup);
  const { data: modelList } = useAcceptedModels(Boolean(isAdmin && selfSignup && status?.has_gateway_key));
  // Seen unfinished in this visit: then the finish is shown before it goes.
  const [seenUnfinished, setSeenUnfinished] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // A just-created gateway key lives only here, for this page visit: whole in
  // its dialog until that is dismissed, and in the agent prompt's copy (shown
  // shortened). Held above the steps: creating it completes step 2, which
  // unmounts the button that created it.
  const [newKey, setNewKey] = useState<string | null>(null);
  const [promptKey, setPromptKey] = useState<string | null>(null);

  useEffect(() => {
    if (status && !status.completed) setSeenUnfinished(true);
  }, [status]);

  if (!isAdmin || !status || !selfSignup || dismissed) return null;
  if (status.completed && !seenUnfinished) return null;

  const step1: StepState = status.has_provider_key ? "done" : "current";
  const step2: StepState = status.has_gateway_key ? "done" : status.has_provider_key ? "current" : "pending";
  const step3: StepState = status.first_request_seen ? "done" : awaitingFirstRequest(status) ? "current" : "pending";

  const models = modelList?.models ?? [];
  const benchmarkDays = status.benchmark_days;

  return (
    <section
      data-testid="onboarding-wizard"
      className="mb-6 space-y-5 rounded-xl border border-line-subtle bg-surface p-6"
    >
      <div>
        <h2 className="text-base font-semibold text-fg">Set up Phantm</h2>
        <p className="text-sm text-fg-muted">
          {benchmarkDays
            ? `Three steps to your first request. Your ${benchmarkDays}-day Benchmark starts then.`
            : "Three steps from here to your first optimized request."}
        </p>
      </div>

      <Step n={1} state={step1} title="Add a provider key">
        {step1 === "done" ? (
          <p className="text-sm text-fg-muted">Saved and checked. You can add more providers in Settings.</p>
        ) : (
          <ProviderKeyForm />
        )}
      </Step>

      <Step n={2} state={step2} title="Create your gateway key and point your code at Phantm">
        {step2 === "current" && (
          <CreateGatewayKey
            onCreated={(gatewayKey) => {
              setNewKey(gatewayKey);
              setPromptKey(gatewayKey);
            }}
          />
        )}
        {step2 === "done" && (
          <IntegrationSetup
            apiBase={status.api_base_url}
            gatewayKey={promptKey}
            snippetModels={snippetModels(models)}
          />
        )}
      </Step>

      <Step n={3} state={step3} title="Send your first request">
        {step3 === "done" ? (
          <div className="space-y-3">
            <p className="text-sm text-fg">
              Your first request arrived. Your {benchmarkDays ? "Benchmark" : "Free week"} has started.
            </p>
            <button type="button" onClick={() => setDismissed(true)} className={primaryCls}>
              Go to your dashboard
            </button>
          </div>
        ) : (
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            {step3 === "current" && <Loader2 size={15} className="mt-0.5 shrink-0 animate-spin text-electric-blue" />}
            <span>
              {step3 === "current" ? "Waiting for your first request… " : ""}
              {benchmarkDays
                ? `Your ${benchmarkDays}-day Benchmark starts with your first request.`
                : `Your Free week starts with your first request: ${lengthLabel(status.free_week_minutes)} optimized, with no Savings fee.`}
            </span>
          </p>
        )}
      </Step>
      {newKey && <KeyOnceModal gatewayKey={newKey} onDismiss={() => setNewKey(null)} />}
    </section>
  );
}

function Step({ n, state, title, children }: { n: number; state: StepState; title: string; children: ReactNode }) {
  return (
    <div data-testid={`wizard-step-${n}`} data-state={state} className="flex gap-3">
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
          state === "done"
            ? "bg-electric-blue text-white"
            : state === "current"
              ? "border border-electric-blue text-electric-blue"
              : "border border-line text-fg-faint"
        }`}
      >
        {state === "done" ? <Check size={13} /> : n}
      </span>
      <div className={`flex-1 space-y-3 ${state === "pending" ? "opacity-50" : ""}`}>
        <h3 className="text-sm font-medium text-fg">{title}</h3>
        {state !== "pending" && children}
      </div>
    </div>
  );
}

function ProviderKeyForm() {
  const save = useSaveProviderKey();
  const [provider, setProvider] = useState<string>("openai");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setError("");
    save.mutate({ provider, apiKey: apiKey.trim() }, { onError: (err) => setError(toUserMessage(err)) });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-sm text-fg-muted">
        Phantm calls your provider with your own key. It's checked with the provider now and stored encrypted.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="wizard-provider" className="text-xs text-fg-muted">Provider</label>
          <select id="wizard-provider" value={provider} onChange={(e) => setProvider(e.target.value)} className={inputCls}>
            {PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex min-w-64 flex-1 flex-col gap-1">
          <label htmlFor="wizard-api-key" className="text-xs text-fg-muted">
            {PROVIDER_LABELS.get(provider)} API key
          </label>
          <input
            id="wizard-api-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={PROVIDERS.find((p) => p.id === provider)?.placeholder}
            className={inputCls}
          />
        </div>
        <button type="submit" disabled={!apiKey.trim() || save.isPending} className={primaryCls}>
          {save.isPending ? "Checking…" : "Save and check"}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-block-red">
          {error}
        </p>
      )}
    </form>
  );
}

function CreateGatewayKey({ onCreated }: { onCreated: (gatewayKey: string) => void }) {
  const rotate = useRotateGatewayKey();
  const [error, setError] = useState("");

  async function create() {
    setError("");
    try {
      const result = await rotate.mutateAsync({ revokeExisting: false });
      onCreated(result.gateway_key);
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">
        Your code sends this key to Phantm instead of your provider key. It's shown once, so keep it somewhere safe.
      </p>
      <button type="button" onClick={create} disabled={rotate.isPending} className={primaryCls}>
        Create your gateway key
      </button>
      {error && (
        <p role="alert" className="text-sm text-block-red">
          {error}
        </p>
      )}
    </div>
  );
}

function IntegrationSetup({
  apiBase,
  gatewayKey,
  snippetModels,
}: {
  apiBase: string;
  gatewayKey: string | null;
  snippetModels: { openai: string; anthropic: string };
}) {
  const snippets = buildSnippets(apiBase, snippetModels);
  return (
    <div className="space-y-4">
      <AgentPrompt apiBase={apiBase} gatewayKey={gatewayKey} />
      <p className="text-sm text-fg-muted">
        Or make the change yourself: replace <code className="text-fg">gw_YOUR_PHANTM_KEY</code> with your gateway key.
        OpenAI SDKs use the <code className="text-fg">/v1</code> address; Anthropic SDKs and Claude Code don't, and take
        the key as an auth token.
      </p>
      <div className="space-y-2">
        {snippets.map((snippet) => (
          <SnippetPanel key={snippet.id} snippet={snippet} />
        ))}
      </div>
    </div>
  );
}

/** The prompt for the Customer's coding agent. With the key just created, the
 * copy carries it whole and the screen shows it shortened; otherwise both use
 * the placeholder. */
function AgentPrompt({ apiBase, gatewayKey }: { apiBase: string; gatewayKey: string | null }) {
  const copied = buildAgentPrompt(apiBase, gatewayKey ?? undefined);
  const shown = gatewayKey ? buildAgentPrompt(apiBase, maskKey(gatewayKey)) : copied;
  return (
    <div data-testid="agent-prompt" className="rounded-lg border border-electric-blue/40 bg-electric-blue/5">
      <div className="flex items-center justify-between gap-3 border-b border-electric-blue/20 px-3 py-2">
        <div>
          <h4 className="text-sm font-medium text-fg">Let your coding agent make the switch</h4>
          <p className="text-xs text-fg-muted">
            Paste this into Claude Code, Cursor, Codex or another coding agent working in your project.
            {gatewayKey
              ? " The copy includes your gateway key, so paste it only into an agent you trust."
              : " Replace gw_YOUR_PHANTM_KEY with your gateway key."}
          </p>
        </div>
        <CopyButton text={copied} label="Copy the prompt" />
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap p-3 text-xs text-fg">
        <code>{shown}</code>
      </pre>
    </div>
  );
}

/** One SDK's setup, folded until its name is clicked. */
function SnippetPanel({ snippet }: { snippet: Snippet }) {
  const [open, setOpen] = useState(false);
  const bodyId = `snippet-body-${snippet.id}`;
  return (
    <div data-snippet={snippet.id} className="rounded-lg border border-line-subtle">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((was) => !was)}
          className="flex flex-1 items-center gap-1.5 text-left text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ChevronRight size={14} className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          {snippet.label}
        </button>
        <CopyButton text={snippet.code} label={`Copy the ${snippet.label} snippet`} />
      </div>
      {open && (
        <pre
          id={bodyId}
          data-testid={`snippet-${snippet.id}`}
          className="overflow-x-auto border-t border-line-subtle p-3 text-xs text-fg"
        >
          <code>{snippet.code}</code>
        </pre>
      )}
    </div>
  );
}
